import type { Milestone, Project, Task } from '@/types';

const STORAGE_KEY = 'ax1capital:runtime-project-records:v1';

interface StoredRuntimeProjectRecords {
  version: 1;
  projects: Project[];
  milestones: Milestone[];
  tasks: Task[];
}

interface RuntimeProjectRecords {
  projects: Map<string, Project>;
  milestones: Map<string, Milestone>;
  tasks: Map<string, Task>;
}

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function reviveDate(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(reviveDate);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, entry]) => [
        key,
        typeof entry === 'string'
          && /^\d{4}-\d{2}-\d{2}T/.test(entry)
          && !Number.isNaN(Date.parse(entry))
          ? new Date(entry)
          : reviveDate(entry),
      ]),
    );
  }
  return value;
}

function emptyRecords(): RuntimeProjectRecords {
  return {
    projects: new Map(),
    milestones: new Map(),
    tasks: new Map(),
  };
}

function loadRecords(): RuntimeProjectRecords {
  const records = emptyRecords();
  const localStorage = storage();
  if (!localStorage) return records;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return records;
    const parsed = JSON.parse(raw) as Partial<StoredRuntimeProjectRecords>;
    if (parsed.version !== 1) return records;

    (Array.isArray(parsed.projects) ? parsed.projects : [])
      .map((project) => reviveDate(project) as Project)
      .forEach((project) => records.projects.set(project.id, project));
    (Array.isArray(parsed.milestones) ? parsed.milestones : [])
      .map((milestone) => reviveDate(milestone) as Milestone)
      .forEach((milestone) => records.milestones.set(milestone.id, milestone));
    (Array.isArray(parsed.tasks) ? parsed.tasks : [])
      .map((task) => reviveDate(task) as Task)
      .forEach((task) => records.tasks.set(task.id, task));
  } catch {
    return emptyRecords();
  }

  return records;
}

let runtimeRecords = loadRecords();

function persist(): void {
  const localStorage = storage();
  if (!localStorage) return;
  const payload: StoredRuntimeProjectRecords = {
    version: 1,
    projects: [...runtimeRecords.projects.values()],
    milestones: [...runtimeRecords.milestones.values()],
    tasks: [...runtimeRecords.tasks.values()],
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

function clone<T>(value: T): T {
  return structuredClone(value);
}

export function createBootstrapProjectId(): string {
  const identity = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `project-${identity}`;
}

export function createBootstrapMilestoneId(): string {
  const identity = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `milestone-${identity}`;
}

export function createBootstrapTaskId(): string {
  const identity = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `task-${identity}`;
}

export function saveBootstrapProject(project: Project): Project {
  runtimeRecords.projects.set(project.id, clone(project));
  persist();
  return clone(project);
}

export function saveBootstrapMilestone(milestone: Milestone): Milestone {
  runtimeRecords.milestones.set(milestone.id, clone(milestone));
  persist();
  return clone(milestone);
}

export function saveBootstrapTask(task: Task): Task {
  runtimeRecords.tasks.set(task.id, clone(task));
  persist();
  return clone(task);
}

export function removeBootstrapMilestone(milestoneId: string): void {
  runtimeRecords.milestones.delete(milestoneId);
  runtimeRecords.tasks.forEach((task, taskId) => {
    if (task.milestoneId === milestoneId) {
      runtimeRecords.tasks.set(taskId, { ...task, milestoneId: undefined, updatedAt: new Date() });
    }
  });
  persist();
}

export function removeBootstrapTask(taskId: string): void {
  runtimeRecords.tasks.delete(taskId);
  persist();
}

export function getBootstrapProjectSnapshot(projectId: string): {
  project: Project | null;
  milestones: Milestone[];
  tasks: Task[];
} {
  return {
    project: runtimeRecords.projects.has(projectId)
      ? clone(runtimeRecords.projects.get(projectId)!)
      : null,
    milestones: [...runtimeRecords.milestones.values()]
      .filter((milestone) => milestone.projectId === projectId)
      .map(clone),
    tasks: [...runtimeRecords.tasks.values()]
      .filter((task) => task.projectId === projectId)
      .map(clone),
  };
}

export function listBootstrapProjects(): Project[] {
  return [...runtimeRecords.projects.values()].map(clone);
}

export function listBootstrapMilestones(): Milestone[] {
  return [...runtimeRecords.milestones.values()].map(clone);
}

export function listBootstrapTasks(): Task[] {
  return [...runtimeRecords.tasks.values()].map(clone);
}

export function clearBootstrapProjectPersistenceForTests(): void {
  runtimeRecords = emptyRecords();
  storage()?.removeItem(STORAGE_KEY);
}

export function reloadBootstrapProjectPersistenceForTests(): void {
  runtimeRecords = loadRecords();
}
